import React from 'react';
import PropTypes from 'prop-types';

import { getTranslation } from 'common/react/utils/translations';

import { useGetMatchEventsQuery } from 'store/modules/api/content-api';
import { useGetLiveStreamsQuery } from 'store/modules/api/content-api';

import { FixtureLinkButton } from './FixtureLinkButton';
import { FixtureMatchReportLink } from './FixtureMatchReportLink';

export const FixtureLinks = ({
    match,
    matchStatus,
    metadata = {},
    broadcastGuideUrl,
    reportLabel
}) => {
    // Parameters to get broadcaster match events for this round
    const params = {
        compseason: match?.compSeason?.id,
        round: match?.round?.roundNumber
    };

    const { shouldShowBroadcaster } = useGetMatchEventsQuery(params, {
        selectFromResult: ({ data }) => ({
            shouldShowBroadcaster: data?.content?.some(
                (matchEvent) =>
                    matchEvent?.channels?.length &&
                    matchEvent.contentReference.providerId === match.providerId
            )
        })
    });

    // Parameters to get broadcaster match events for this round
    const liveStreamParams = {
        compseason: match?.compSeason?.id,
        round: match?.round?.roundNumber
    };

    /** Fetch live video livestreams for the round, then check to see if there
     * is one for this match with the right attributes */
    const { liveStream } = useGetLiveStreamsQuery(liveStreamParams, {
        selectFromResult: ({ data }) => ({
            liveStream: data?.content?.find(
                (stream) =>
                    stream?.streams?.length &&
                    stream?.matchId === match.providerId &&
                    stream?.streams?.some((item) =>
                        item.customAttributes?.some(
                            (attr) =>
                                attr.attrName === 'AFL Live Video' &&
                                attr.attrValue === 'AFL Live Video'
                        )
                    )
            )
        })
    });

    // Pull out the right stream item
    const liveStreamStream = liveStream?.streams?.find((item) =>
        item.customAttributes?.some(
            (attr) =>
                attr.attrName === 'AFL Live Video' &&
                attr.attrValue === 'AFL Live Video'
        )
    );

    // create a live stream url for video modal if a live stream exists for the fixture
    const streamUrl = liveStream
        ? PULSE.app.common.url.getLiveStreamUrl(liveStreamStream)
        : null;

    const hasFreeEntry = metadata.free_entry === 'true' ? true : false;

    const renderUpcomingTicketLinks = () => {
        let ticketLink = {};

        if (metadata.ticket_link && !hasFreeEntry) {
            ticketLink = {
                icon: 'tickets-new',
                label: getTranslation('label.fixtures.buy-tickets'),
                href: metadata.ticket_link,
                target: '_blank'
            };

            return <FixtureLinkButton {...ticketLink} />;
        }
    };

    const renderUpcomingOrLiveTicketLinks = () => {
        let ticketLink = {};
        let liveStreamLink = {};

        // only show a broadcast guide link if the match is assigned to a broadcaster match-event
        // show Watch Live button if match is live and stream exists
        if (
            broadcastGuideUrl &&
            shouldShowBroadcaster &&
            matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE &&
            streamUrl
        ) {
            ticketLink = {
                icon: 'live-broadcast',
                label: getTranslation('label.fixtures.broadcastGuide'),
                href: broadcastGuideUrl,
                target: '_blank',
                theme: 'live'
            };

            liveStreamLink = {
                icon: 'video-playback-new',
                label: getTranslation('label.watch-live'),
                href: streamUrl,
                target: '_blank',
                cssClass: 'u-hide-from-desktop',
                theme: 'live'
            };

            return (
                <>
                    <FixtureLinkButton {...liveStreamLink} />
                    <FixtureLinkButton {...ticketLink} />
                </>
            );
        }

        // only show a broadcast guide link if the match is assigned to a broadcaster match-event
        if (
            broadcastGuideUrl &&
            shouldShowBroadcaster &&
            (!streamUrl ||
                matchStatus !== PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE)
        ) {
            ticketLink = {
                icon: 'live-broadcast',
                label: getTranslation('label.fixtures.broadcastGuide'),
                href: broadcastGuideUrl,
                target: '_blank'
            };

            return <FixtureLinkButton {...ticketLink} />;
        }

        // show Watch Live button if no broadcast guide, stream exists and match is live
        if (
            (!broadcastGuideUrl || !shouldShowBroadcaster) &&
            matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE &&
            streamUrl
        ) {
            liveStreamLink = {
                icon: 'video-playback-new',
                label: getTranslation('label.watch-live'),
                href: streamUrl,
                target: '_blank',
                cssClass: 'u-hide-from-desktop',
                theme: 'live'
            };

            return <FixtureLinkButton {...liveStreamLink} />;
        }
    };

    const renderUpcomingTravelLinks = () => {
        let travelLink = {};

        if (metadata.travel_link) {
            travelLink = {
                icon: 'plane',
                label: getTranslation('label.fixtures.travel'),
                href: metadata.travel_link,
                target: '_blank'
            };

            return <FixtureLinkButton {...travelLink} />;
        }
    };

    return (
        <>
            {matchStatus ===
                PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED && ( // check match status
                <FixtureMatchReportLink
                    match={match}
                    reportLabel={reportLabel}
                />
            )}

            {matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING &&
                renderUpcomingTicketLinks()}

            {(matchStatus ===
                PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING ||
                matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE) &&
                renderUpcomingOrLiveTicketLinks()}

            {matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING &&
                renderUpcomingTravelLinks()}
        </>
    );
};

FixtureLinks.propTypes = {
    match: PropTypes.object,
    matchStatus: PropTypes.string,
    metadata: PropTypes.object,
    regionIsAus: PropTypes.bool,
    reportLabel: PropTypes.string,
    broadcastGuideUrl: PropTypes.string
};
